import React, { useState, useMemo, useEffect } from 'react';
import { Calculator, ChevronRight, AlertCircle, ArrowUp } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard } from '../PageLayout';

interface Sparte {
  name: string;
  absatz: number;
  stornoquote: number;
  schadensquote: number;
  bestandsentwicklung: number;
}

interface BonistufeData {
  ws: number;
  boni: number;
}

interface TabData {
  agenturname: string;
  taetigkeitsbeginn: number;
  standPerMonat: number;
  sparten: Record<string, Sparte>;
  agenturstufe: string;
}

const DEFAULT_FORM_DATA: TabData = {
  agenturname: 'Max Muster',
  taetigkeitsbeginn: 1,
  standPerMonat: 12,
  sparten: {
    'KV': { name: 'KV (o. Pflege)', absatz: 0, stornoquote: 0, schadensquote: 0, bestandsentwicklung: 56825 },
    'LV': { name: 'LV (inkl. Dynamik WS)', absatz: 0, stornoquote: 0, schadensquote: 0, bestandsentwicklung: -7117 },
    'U': { name: 'U (m.E.)', absatz: 0, stornoquote: 0, schadensquote: 0, bestandsentwicklung: -1767 },
    'STHRS': { name: 'STH/RS', absatz: 0, stornoquote: 0, schadensquote: 0, bestandsentwicklung: 55897 },
    'BS': { name: 'BS', absatz: 0, stornoquote: 0, schadensquote: 0, bestandsentwicklung: 0 },
  },
  agenturstufe: 'Generalagentur II',
};

// Bonitafeln aus der Excel-Datei
const BONITAFELN: Record<string, Record<string, BonistufeData[]>> = {
  'Agentur': {
    'KV': [
      { ws: 500, boni: 220 },
      { ws: 800, boni: 320 },
      { ws: 1200, boni: 480 },
      { ws: 1600, boni: 750 },
    ],
    'LV': [
      { ws: 800, boni: 220 },
      { ws: 1200, boni: 320 },
      { ws: 1700, boni: 480 },
      { ws: 2300, boni: 750 },
    ],
    'U': [
      { ws: 450, boni: 530 },
      { ws: 630, boni: 770 },
      { ws: 820, boni: 1200 },
      { ws: 1200, boni: 1700 },
    ],
    'STHRS': [
      { ws: 300, boni: 320 },
      { ws: 600, boni: 470 },
      { ws: 900, boni: 680 },
      { ws: 1200, boni: 1100 },
    ],
    'BS': [
      { ws: 250, boni: 85 },
      { ws: 370, boni: 125 },
      { ws: 480, boni: 190 },
      { ws: 600, boni: 290 },
    ],
  },
  'Hauptagentur': {
    'KV': [
      { ws: 750, boni: 320 },
      { ws: 1200, boni: 530 },
      { ws: 1700, boni: 840 },
      { ws: 2300, boni: 1300 },
    ],
    'LV': [
      { ws: 1200, boni: 320 },
      { ws: 1900, boni: 530 },
      { ws: 2800, boni: 840 },
      { ws: 3600, boni: 1300 },
    ],
    'U': [
      { ws: 450, boni: 620 },
      { ws: 700, boni: 930 },
      { ws: 950, boni: 1500 },
      { ws: 1200, boni: 2100 },
    ],
    'STHRS': [
      { ws: 450, boni: 370 },
      { ws: 870, boni: 580 },
      { ws: 1300, boni: 840 },
      { ws: 1800, boni: 1300 },
    ],
    'BS': [
      { ws: 500, boni: 100 },
      { ws: 800, boni: 150 },
      { ws: 1100, boni: 250 },
      { ws: 1400, boni: 350 },
    ],
  },
  'Generalagentur I': {
    'KV': [
      { ws: 1000, boni: 740 },
      { ws: 1900, boni: 1200 },
      { ws: 3000, boni: 1750 },
      { ws: 4500, boni: 2400 },
    ],
    'LV': [
      { ws: 2100, boni: 740 },
      { ws: 3300, boni: 1200 },
      { ws: 4800, boni: 1800 },
      { ws: 6500, boni: 2500 },
    ],
    'U': [
      { ws: 800, boni: 1500 },
      { ws: 1300, boni: 2200 },
      { ws: 1800, boni: 2800 },
      { ws: 2500, boni: 3500 },
    ],
    'STHRS': [
      { ws: 1200, boni: 890 },
      { ws: 2000, boni: 1400 },
      { ws: 3000, boni: 2000 },
      { ws: 4200, boni: 2800 },
    ],
    'BS': [
      { ws: 750, boni: 250 },
      { ws: 1200, boni: 350 },
      { ws: 1700, boni: 450 },
      { ws: 2200, boni: 600 },
    ],
  },
  'Generalagentur II': {
    'KV': [
      { ws: 1200, boni: 850 },
      { ws: 2200, boni: 1400 },
      { ws: 3500, boni: 2000 },
      { ws: 5000, boni: 2800 },
    ],
    'LV': [
      { ws: 2500, boni: 900 },
      { ws: 3900, boni: 1450 },
      { ws: 5500, boni: 2100 },
      { ws: 7500, boni: 3000 },
    ],
    'U': [
      { ws: 1000, boni: 1700 },
      { ws: 1600, boni: 2500 },
      { ws: 2200, boni: 3200 },
      { ws: 3000, boni: 4000 },
    ],
    'STHRS': [
      { ws: 1500, boni: 1050 },
      { ws: 2400, boni: 1650 },
      { ws: 3500, boni: 2400 },
      { ws: 4800, boni: 3300 },
    ],
    'BS': [
      { ws: 1000, boni: 300 },
      { ws: 1500, boni: 425 },
      { ws: 2000, boni: 575 },
      { ws: 2700, boni: 750 },
    ],
  },
  'Bezirksdirektor': {
    'KV': [
      { ws: 1500, boni: 1050 },
      { ws: 2700, boni: 1700 },
      { ws: 4200, boni: 2400 },
      { ws: 6000, boni: 3300 },
    ],
    'LV': [
      { ws: 3000, boni: 1100 },
      { ws: 4700, boni: 1800 },
      { ws: 6500, boni: 2600 },
      { ws: 9000, boni: 3700 },
    ],
    'U': [
      { ws: 1200, boni: 2000 },
      { ws: 1950, boni: 3000 },
      { ws: 2700, boni: 3900 },
      { ws: 3700, boni: 4800 },
    ],
    'STHRS': [
      { ws: 1800, boni: 1250 },
      { ws: 2900, boni: 2000 },
      { ws: 4200, boni: 2900 },
      { ws: 5700, boni: 4000 },
    ],
    'BS': [
      { ws: 1200, boni: 375 },
      { ws: 1800, boni: 525 },
      { ws: 2400, boni: 700 },
      { ws: 3200, boni: 925 },
    ],
  },
};

const BoniRechnerView: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [activeTab, setActiveTab] = useState<'eingabe' | 'vp-bonifikation' | 'bestandsausschoepfung'>('eingabe');

  const [formData, setFormData] = useState<TabData>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('boni_rechner_form');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          return {
            ...DEFAULT_FORM_DATA,
            ...parsed,
            sparten: {
              ...DEFAULT_FORM_DATA.sparten,
              ...(parsed.sparten || {}),
            },
          };
        } catch {
          return DEFAULT_FORM_DATA;
        }
      }
    }
    return DEFAULT_FORM_DATA;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('boni_rechner_form', JSON.stringify(formData));
    }
  }, [formData]);

  const getTaetigkeitsMonate = () => {
    return formData.standPerMonat - formData.taetigkeitsbeginn + 1;
  };

  const calculateBonistufe = (absatz: number, bonistaffeln: BonistufeData[]) => {
    let stufe = 0;
    for (let i = 0; i < bonistaffeln.length; i++) {
      if (absatz >= bonistaffeln[i].ws) {
        stufe = i + 1;
      }
    }
    return stufe;
  };

  const calculateBoniAmount = (absatz: number, bonistaffeln: BonistufeData[], taetMonate: number) => {
    if (absatz === 0) return 0;
    const stufe = calculateBonistufe(absatz, bonistaffeln);
    if (stufe === 0) return 0;
    return (bonistaffeln[stufe - 1].boni * taetMonate) / 12;
  };

  const bonitafeln = BONITAFELN[formData.agenturstufe] || BONITAFELN['Agentur'];
  const taetMonate = getTaetigkeitsMonate();

  const boniberechnung = useMemo(() => {
    const result: Record<string, { stufe: number; boni: number; qualFaktor: number }> = {};
    
    Object.entries(formData.sparten).forEach(([key, sparte]) => {
      const staffeln = bonitafeln[key] || [];
      const stufe = calculateBonistufe(sparte.absatz, staffeln);
      let boniAmount = calculateBoniAmount(sparte.absatz, staffeln, taetMonate);
      
      // Qualitätsfaktoren anwenden
      let qualFaktor = 100;
      
      // Stornoquote
      if (key === 'KV' && sparte.stornoquote < 1) qualFaktor = 130;
      if (key === 'KV' && sparte.stornoquote > 5) qualFaktor = 70;
      if (key === 'LV' && sparte.stornoquote < 5) qualFaktor = 130;
      if (key === 'LV' && sparte.stornoquote > 18) qualFaktor = 70;
      
      // Schadensquote
      if (key === 'STHRS' && sparte.schadensquote < 20) qualFaktor = 130;
      if (key === 'STHRS' && sparte.schadensquote > 40) qualFaktor = 70;
      
      // Bestandsentwicklung
      if (sparte.bestandsentwicklung >= 0) qualFaktor = Math.max(qualFaktor, 120);
      if (sparte.bestandsentwicklung < 0) qualFaktor = Math.min(qualFaktor, 80);
      
      boniAmount = (boniAmount * qualFaktor) / 100;
      
      result[key] = { stufe, boni: boniAmount, qualFaktor };
    });
    
    return result;
  }, [formData.sparten, bonitafeln, taetMonate]);

  const totalBoni = useMemo(() => {
    return Object.values(boniberechnung).reduce((sum, item) => sum + (item.boni || 0), 0);
  }, [boniberechnung]);

  const handleInputChange = (key: keyof TabData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSparteChange = (sparte: string, field: keyof Sparte, value: any) => {
    setFormData(prev => ({
      ...prev,
      sparten: {
        ...prev.sparten,
        [sparte]: {
          ...prev.sparten[sparte],
          [field]: field === 'name' ? value : (isNaN(parseFloat(value)) ? 0 : parseFloat(value)),
        },
      },
    }));
  };

  const tabs = [
    { id: 'eingabe', label: 'Eingabe' },
    { id: 'vp-bonifikation', label: 'VP Bonifikation' },
    { id: 'bestandsausschoepfung', label: 'Bestandsauschöpfung' },
  ];

  return (
    <PageLayout>
      <PageHeader title="Boni-Rechner" subtitle="Signal Iduna Provisionsrechner" />

      <PageCard className="p-2">
      <div className="flex gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? isDark
                  ? 'bg-gradient-to-r from-amber-500/40 to-orange-500/40 text-white border border-amber-400/50'
                  : 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 border border-amber-300'
                : isDark
                  ? 'text-white/60 hover:text-white/80'
                  : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && <ChevronRight size={18} />}
          </button>
        ))}
      </div>
      </PageCard>

      {/* Eingabe Tab */}
      {activeTab === 'eingabe' && (
        <div className="space-y-6">
          {/* Agentur Info */}
          <div className={`border rounded-2xl p-6 ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Agentur Informationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  Name der Agentur
                </label>
                <input
                  type="text"
                  value={formData.agenturname}
                  onChange={e => handleInputChange('agenturname', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  Tätigkeitsbeginn (Monat)
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.taetigkeitsbeginn || ''}
                  onChange={e => handleInputChange('taetigkeitsbeginn', parseInt(e.target.value) || 1)}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  Stand per Monat
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.standPerMonat || ''}
                  onChange={e => handleInputChange('standPerMonat', parseInt(e.target.value) || 12)}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  Agenturstufe
                </label>
                <select
                  value={formData.agenturstufe}
                  onChange={e => handleInputChange('agenturstufe', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                >
                  <option>Agentur</option>
                  <option>Hauptagentur</option>
                  <option>Generalagentur I</option>
                  <option>Generalagentur II</option>
                  <option>Bezirksdirektor</option>
                </select>
              </div>
            </div>
            <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-amber-500/20 border border-amber-400/30' : 'bg-amber-50 border border-amber-200'}`}>
              <p className={`font-bold ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                Tätigkeitsmonate: {taetMonate} (von Monat {formData.taetigkeitsbeginn} bis {formData.standPerMonat})
              </p>
            </div>
          </div>

          {/* Sparten Eingabe */}
          <div className={`border rounded-2xl p-6 ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Sparten Daten</h3>
            <div className="space-y-4">
              {Object.entries(formData.sparten).map(([key, sparte]) => (
                <div key={key} className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                        {sparte.name}
                      </label>
                      <input
                        type="number"
                        placeholder="Absatz WS"
                        value={sparte.absatz || ''}
                        onChange={e => handleSparteChange(key, 'absatz', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                      />
                    </div>
                    {key !== 'BS' && (
                      <>
                        <div>
                          <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            Stornoquote %
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            placeholder="Storno %"
                            value={sparte.stornoquote || ''}
                            onChange={e => handleSparteChange(key, 'stornoquote', e.target.value)}
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                          />
                        </div>
                        {key === 'STHRS' && (
                          <div>
                            <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                              Schadensquote %
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="Schaden %"
                              value={sparte.schadensquote || ''}
                              onChange={e => handleSparteChange(key, 'schadensquote', e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                        Bestandsentwicklung
                      </label>
                      <input
                        type="number"
                        placeholder="Bestand"
                        value={sparte.bestandsentwicklung || ''}
                        onChange={e => handleSparteChange(key, 'bestandsentwicklung', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                      />
                    </div>
                    <div className={`flex items-end justify-center p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                      <span className={`text-sm font-bold ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                        {boniberechnung[key]?.boni.toFixed(2) || '0.00'} €
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* VP Bonifikation Tab */}
      {activeTab === 'vp-bonifikation' && (
        <div className="space-y-6">
          <div className={`border rounded-2xl p-6 ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-xl font-bold mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <ArrowUp size={24} className={isDark ? 'text-green-400' : 'text-green-600'} />
              Provisionsübersicht
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(boniberechnung).map(([key, calc]) => (
                <div key={key} className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                    {formData.sparten[key]?.name}
                  </p>
                  <p className={`text-2xl font-bold mb-2 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                    {calc.boni.toFixed(2)} €
                  </p>
                  <div className={`text-xs space-y-1 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                    <p>Stufe: {calc.stufe}</p>
                    <p>Qualfaktor: {calc.qualFaktor}%</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Gesamtbonus */}
            <div className={`mt-6 p-6 rounded-2xl ${isDark ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300'}`}>
              <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-green-300' : 'text-green-700'}`}>GESAMTBONUS</p>
              <p className={`text-4xl font-bold ${isDark ? 'text-green-200' : 'text-green-900'}`}>
                {totalBoni.toFixed(2)} €
              </p>
              <p className={`text-xs mt-2 ${isDark ? 'text-green-300/80' : 'text-green-800'}`}>
                für {taetMonate} Tätigkeitsmonate bei Agenturstufe: {formData.agenturstufe}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bestandsauschöpfung Tab */}
      {activeTab === 'bestandsausschoepfung' && (
        <div className={`border rounded-2xl p-6 ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Bestandsauschöpfung</h3>
          <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/20 border border-blue-400/30' : 'bg-blue-50 border border-blue-200'} flex items-start gap-3`}>
            <AlertCircle size={20} className={isDark ? 'text-blue-300 flex-shrink-0 mt-1' : 'text-blue-700 flex-shrink-0 mt-1'} />
            <div>
              <p className={`font-semibold ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>Bestandsauschöpfung wird analysiert</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-blue-300/80' : 'text-blue-800'}`}>
                Die Bestandsauschöpfungsanalyse zeigt, wie gut der verfügbare Bestand ausgeschöpft wird und welche Optimierungspotenziale vorhanden sind.
              </p>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default BoniRechnerView;
